<template>
  <div class="bg-light min-vh-100 pb-4">
    <!-- Header -->
    <header class="bg-white shadow-sm">
      <div class="container py-3">
        <div class="d-flex justify-content-between align-items-center">
          <h1 class="h3 mb-0 font-weight-bold">Image Editor</h1>
          <div class="d-flex">
            <router-link
                :to="{ name: 'business.image-editor-dashboard' }"
                class="btn btn-outline-dark btn-sm mr-2"
            >
              <i class="uil uil-arrow-left"></i> Dashboard
            </router-link>
            <help-modal :is-open="showHelp" :on-close="() => showHelp = false" />
          </div>
        </div>
      </div>
    </header>

    <!-- Main Content -->
    <main class="container mt-4">
      <div class="row">
        <!-- Left Column: Image Upload/Preview -->
        <div class="col-lg-6">
          <!-- Image Uploader/Preview Section -->
          <div class="card mb-4">
            <div class="card-body">
              <div class="mb-3">
                <h3 class="h5 mb-3">1. Upload Your Image</h3>
                <!-- Image Upload button always visible in the top left -->
                <div v-if="imagePreview">
                  <simple-image-uploader
                      ref="imageUploader"
                    :initial-image="imagePreview"
                    :image-id="id"
                    @image-selected="handleFileSelected"
                    @image-reset="resetImage"
                  />
                </div>
              </div>

              <!-- Image Uploader (when no image is uploaded) -->
              <div v-if="!imagePreview" class="mb-3">
                <simple-image-uploader
                  @image-selected="handleFileSelected"
                />
              </div>

              <h3 class="h5 mb-3">2. Customize Overlay Text</h3>
              <simple-overlay-customizer
                  :settings="overlaySettings"
                  @settings-change="handleOverlaySettingsChange"
              />
              <h3 class="h5 mb-3">3. Additional Layers <button class="btn btn-xs btn-info" @click="ADD_LAYER"><i class="uil uil-plus-circle"></i> Add Layer</button></h3>
              <table v-if="overlaySettings.layers.length" class="table table-striped">
                <tr v-for="(layer,i) in overlaySettings.layers" :key="i">
                  <td class="p-0 pt-3">
                    <div class="mb-2">
                      <strong>Layer {{i + 1}}.</strong> <button class="btn btn-danger btn-xs" @click="DELETE_LAYER(layer)">Delete</button>
                    </div>
                    <input v-model="layer.text" type="text" class="form-control" @input="updateOverlaySettings" />
                    <div class="mt-3">
                      <div class="row">
                        <div class="col-md-6">
                          <b-form-group label="Background Color" label-class="control-label-bold">
                            <div class="color-picker-container d-flex">
                              <b-form-input
                                  v-model="layer.bgColor"
                                  type="color"
                                  class="color-picker"
                                  @input="updateOverlaySettings"
                              ></b-form-input>
                              <b-form-input
                                  v-model="layer.bgColor"
                                  type="text"
                                  class="color-code ml-2"
                                  @input="updateOverlaySettings"
                              ></b-form-input>
                            </div>
                          </b-form-group>
                        </div>
                        <div class="col-md-6">
                          <b-form-group :label="`Background Opacity: ${(layer.bgOpacity * 100).toFixed(0) }%`" label-class="control-label-bold">
                            <b-form-input
                                v-model.number="layer.bgOpacity"
                                name="bg-opacity"
                                data-lpignore="true"
                                type="range"
                                min="0"
                                max="1"
                                step="0.01"
                                @input="updateOverlaySettings"
                            ></b-form-input>
                          </b-form-group>
                        </div>
                        <div class="col-md-6">
                          <b-form-group label="Text Color" label-class="control-label-bold">
                            <div class="color-picker-container d-flex">
                              <b-form-input
                                  v-model="layer.textColor"
                                  type="color"
                                  class="color-picker"
                                  @input="updateOverlaySettings"
                              ></b-form-input>
                              <b-form-input
                                  v-model="layer.textColor"
                                  type="text"
                                  class="color-code ml-2"
                                  @input="updateOverlaySettings"
                              ></b-form-input>
                            </div>
                          </b-form-group>
                        </div>
                        <div class="col-md-6">
                          <div class="flex justify-between items-center mb-1">
                            <label for="font-size" class="block text-sm font-weight-normal">
                              Font Size: {{ layer.fontSize }}px
                            </label>
                          </div>
                          <b-form-group label-class="control-label-bold">
                            <b-form-input
                                v-model.number="layer.fontSize"
                                name="layer-font-size"
                                type="range"
                                min="12"
                                max="72"
                                step="1"
                                @input="updateOverlaySettings"
                            ></b-form-input>
                          </b-form-group>
                        </div>

                      </div>

                    </div>


                  </td>
                </tr>
              </table>
              <div v-else class="mb-3 font-italic">Add a New Layer to Start</div>
              <!-- Employee -->
              <div class="card">
                <div class="card-body border rounded-lg" style="border-color: #ccc; border-width: 2px !important;">
                  <div class="d-block">
                    <div class="employee-header">
                      <h3 class="text-2xl font-weight-semibold flex items-center mb-0"><svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-users"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path><circle cx="9" cy="7" r="4"></circle><path d="M22 21v-2a4 4 0 0 0-3-3.87"></path><path d="M16 3.13a4 4 0 0 1 0 7.75"></path></svg><span class="pl-2">Employee Assignment</span></h3>
                      <p class="text-sm text-muted">Assign this image to an employee for better organization</p>
                    </div>
                    <select v-model="localEmployeeId" class="form-control">
                      <option value="">No Employee (Unassigned)</option>
                      <option v-for="employee in employees" :key="employee.id" :value="employee.id">
                        {{ employee.name }}
                      </option>
                    </select>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <!-- Save & Sharing Options -->
          <div v-if="imagePreview && id" class="card">
            <div class="card-body">
              <h2 class="h4 mb-3 text-black">Send Test Email</h2>
              <email-tester :image-id="id"/>
            </div>
          </div>
          <div v-if="imagePreview && id" class="card">
            <div class="card-body">
              <h2 class="h4 mb-3 text-black">Send Test Text Message</h2>
              <text-message-tester :image-id="id" />
            </div>
          </div>
        </div>

        <!-- Right Column: Options -->
        <div class="col-lg-6">
          <!-- Help Card (when no image) -->
          <div v-if="!imagePreview" class="card mb-4">
            <div class="card-body">
              <h3 class="h5 mb-3">Preview</h3>
              <div class="square rounded overflow-hidden shadow">
                <div class="square-content text-center p-3 text-black">
                  <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-image mb-2">
                    <rect width="18" height="18" x="3" y="3" rx="2" ry="2"></rect>
                    <circle cx="9" cy="9" r="2"></circle>
                    <path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21"></path>
                  </svg>
                  <p class="text-black font-size-16">Upload an image to preview</p>
                </div>
              </div>

            </div>
          </div>
          <!-- Image Preview with Overlay -->
          <div v-else class="lg-sticky">
            <div class="card">
              <div class="card-body">
                <simple-image-preview
                    ref="previewImage"
                    :image-url="imagePreview"
                    :overlay-settings="overlaySettings"
                    :recipient-name="recipientName"
                    @position-change="handlePositionChange"
                />

                <!-- Download      -->
                <div v-if="imagePreview" class="text-center mt-4">
                  <b-btn type="button" variant="dark-blue" @click="downloadImage"><i class="uil uil-image-download" /> Download (600x600)</b-btn>
                </div>
                <div v-if="imagePreview">
                  <div class="text-center mt-4 h5">Instructions</div>
                  <ul>
                    <li>Upload a square image or use the crop tool</li>
                    <li>Customize the text overlay with your greeting</li>
                    <li>Drag the overlay to position it</li>
                    <li>Download or send as email when ready</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="position-fixed" style="bottom: 1rem; right: 1rem; z-index: 1050;">
        <!-- Initially, just show the Save button -->
        <div v-if="!showForm">
          <button class="btn btn-primary btn-md" @click="showForm = true">
            <i class="uil uil-file-upload" aria-hidden="true"></i> Save to Dashboard
          </button>
        </div>
        <!-- When the button is pressed, show the form with an input, Cancel, and Save -->
        <form v-else class="d-flex flex-column flex-md-row align-items-center bg-white p-2 rounded shadow border" @submit.prevent="saveImage">
          <input
              v-model="imageName"
              type="text"
              class="form-control mr-md-2 mb-2 mb-md-0"
              placeholder="Enter image name"
              required
          >
          <div style="flex:none">
            <button type="button" class="btn btn-outline-dark mr-2" @click="showForm = false">
              Cancel
            </button>
            <button type="submit" class="btn btn-dark-blue" :disabled="!imageName">
              <b-spinner v-if="isSaving" small class="mr-1"></b-spinner>
              <i v-else class="uil uil-file-upload" aria-hidden="true"></i> Save
            </button>
          </div>
        </form>
      </div>
    </main>
  </div>
</template>

<script>
import { mapState, mapMutations, mapActions } from 'vuex';

import SimpleImageUploader from '../components/SimpleImageUploader.vue';
import SimpleImagePreview from '../components/SimpleImagePreview.vue';
import SimpleOverlayCustomizer from '../components/SimpleOverlayCustomizer.vue';
import EmailTester from '../components/EmailTester.vue';
import TextMessageTester from '../components/TextMessageTester.vue';
import HelpModal from '../components/HelpModal.vue';
import {
  BSpinner,
} from 'bootstrap-vue';
import Vue from "vue";

export default {
  name: 'ImageEditor',
  components: {
    // Vue 2 specific components
    SimpleImageUploader,
    SimpleImagePreview,
    SimpleOverlayCustomizer,
    EmailTester,
    TextMessageTester,
    HelpModal,
    // Bootstrap Vue components
    BSpinner,
  },
  data() {
    return {
      imageName: '',
      isSaving: false,
      saveError: '',
      saveSuccess: '',
      showHelp: false,
      showForm:false,
      id:null
    };
  },
  computed: {
    ...mapState('imageEditor',[
      'employeeId',
      'imagePreview',
      'editingImageId',
      'overlaySettings',
      'recipientName'
    ]),
    localEmployeeId: {
      get() {
        return this.employeeId;
      },
      set(value) {
        this.UPDATE_EMPLOYEE_ID(value);
      }
    },
    employees() {
      return this.$store.getters['employees/all'] || []
    },
    user() {
      return this.$store.getters['auth/user']
    },
  },
  async mounted() {
    // Load saved image if editing
    this.RESET_EDITOR(null);
    const id = this.$route.params.id;
    this.id = id;
    if (id) {
      this.$store
          .dispatch('imageEditor/getImage',{ id:id, businessId:this.user.business.id })
          .then((data) => {
              this.imageName = data.name;
          }).catch((err) => {
            if (err) {
              console.log(err);
            }
          })
    }
    this.getEmployees();
  },
  beforeDestroy() {
    // Clear editing state when leaving the editor
    if (!this.$router.currentRoute.path.includes('/editor')) {
      this.SET_EDITING_IMAGE_ID(null);
    }
  },
  methods: {
    ...mapMutations('imageEditor',[
      'SET_IMAGE_FILE',
      'SET_IMAGE_PREVIEW',
      'SET_EDITING_IMAGE_ID',
      'UPDATE_OVERLAY_POSITION',
      'UPDATE_OVERLAY_TEXT',
      'UPDATE_OVERLAY_FONT_SIZE',
      'UPDATE_OVERLAY_STYLE',
      'UPDATE_OVERLAY_SHAPE',
      'UPDATE_BRAND_COLOR',
      'UPDATE_OVERLAY_ROTATION',
      'UPDATE_ANIMATION_ENABLED',
      'UPDATE_ANIMATION_TYPE',
      'UPDATE_ANIMATION_SPEED',
      'RESET_EDITOR',
      'UPDATE_EMPLOYEE_ID',
      'ADD_LAYER',
      'DELETE_LAYER'
    ]),
    ...mapActions('imageEditor', ['loadSavedImage']),
    downloadImage(){
      this.$refs.previewImage.downloadCombinedImage();
    },
    updateOverlaySettings() {
      this.$emit('settings-change', this.overlaySettings);
    },
    getEmployees(){
      this.loadingEmployees = true

      this.$store
          .dispatch('employees/getAll', {
            queries: {
              per_page: 100,
              page: 1,
              search: null,
              sort_by: 'id',
              sort_desc: true,
            },
            page: 'index',
          })
          .then((res) => {
            this.loadingEmployees = false
          })
          .catch(() => {
            this.loadingEmployees = false
          })
    },
    // Handler for SimpleImageUploader component
    handleFileSelected(file, previewUrl) {
      console.log('File selected:', file.name);
      this.SET_IMAGE_FILE(file);
      this.SET_IMAGE_PREVIEW(previewUrl);
    },

    // Handler for resetting the image
    resetImage() {
      this.SET_IMAGE_FILE(null);
      this.SET_IMAGE_PREVIEW(null);
      this.SET_EDITING_IMAGE_ID(null);
    },

    // Handler for position changes from SimpleImagePreview
    handlePositionChange(position) {
      this.UPDATE_OVERLAY_POSITION(position);
    },

    // Handler for overlay settings changes from SimpleOverlayCustomizer
    handleOverlaySettingsChange(settings) {
      // Update all relevant overlay settings
      this.UPDATE_OVERLAY_TEXT(settings.text);
      this.UPDATE_OVERLAY_FONT_SIZE(settings.fontSize);
      this.UPDATE_OVERLAY_STYLE(settings.style);
      this.UPDATE_OVERLAY_SHAPE(settings.shape);

      if (settings.style === 'branded') {
        this.UPDATE_BRAND_COLOR(settings.brandColor);
      }

      this.UPDATE_OVERLAY_ROTATION(settings.rotation);

      // Animation settings
      this.UPDATE_ANIMATION_ENABLED(settings.animation.enabled);
      this.UPDATE_ANIMATION_TYPE(settings.animation.animationType);
      this.UPDATE_ANIMATION_SPEED(settings.animation.speed);

      // Layers
      this.UPDATE_LAYERS(settings.layers);
    },
    saveImage() {
      if (!this.imagePreview) {
        Vue.$toast.open({
          message: 'Please upload an image first',
          type: 'error',
          duration: 5000,
        })
        return;
      }

      if (!this.imageName.trim()) {
        Vue.$toast.open({
          message: 'Please enter a name for your image',
          type: 'error',
          duration: 5000,
        })
        return;
      }

      this.isSaving = true;
      this.saveError = '';
      this.saveSuccess = '';

      try {
        const payload = {
          id: this.id,
          name: this.imageName,
          image: this.imagePreview,
          settings: this.overlaySettings,
          business_id: this.user.business.id,
          employee_id: this.employeeId,
        };
        // Update existing image or create new one
        this.$store
            .dispatch('imageEditor/saveImage', payload)
            .then((data) => {
              this.isSaving = false;
              this.showForm = false;
              if(this.id !== data.id) {
                this.$router.push({name: 'business.image-editor', 'params': {id: data.id}})
              }
            })
            .catch((err) => {
              if (err) {
                console.log(err);
              }
             /* Vue.$toast.open({
                message: err.response.data.errors,
                type: 'error',
                duration: 5000,
              }) */
              this.isSaving = false
            })
      } catch (error) {
        console.error('Error saving image:', error);
        this.saveError = 'Failed to save image. Please try again.';
      }
    }
  }
};
</script>
<style scoped>
.color-picker {
  width: 50px;
  padding: 0;
  height: 38px;
}
[name="bg-opacity"],
[name="layer-font-size"]{
  margin-top: 9px;
}
</style>
<style>
.square {
  position: relative;
  width: 100%;
  padding-top: 100%; /* Creates a 1:1 aspect ratio */
}

.square-content {
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
}
@media (min-width: 992px) {
  .lg-sticky {
    position: sticky;
    top: 5rem;
  }
}
.business-image-editor .content-page, .business-image-editor #wrapper{
  overflow: visible !important;
}
</style>
